import {Button, Col, Drawer, Form, Input, Modal, notification, Row, Select, Upload, UploadFile} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import {Option} from 'antd/es/mentions';
import React, {useEffect, useState} from 'react';
import {ActionType} from '../../constants/actionType';
import {createCharacter, updateCharacter} from '../../helpers/api/story';
import './CharacterDrawer.scss';
import CharacterProperty from './CharacterProperty';
import {RcFile, UploadProps} from "antd/es/upload";
import {Icon} from "ts-react-feather-icons";
import {convertToWebP} from "../../utils/image";

const characterDrawerValidation = {
    name: [
        {required: true, message: 'Name is required'},
        {max: 100, message: 'Name must be less than 100 characters'},
    ],
    status: [
        {required: true, message: 'Status is required'},
    ],
    type: [
        {required: true, message: 'Type is required'},
    ],
    generalInformation: [
        {required: true, message: 'General information is required'},
        {max: 5000, message: 'General information must be less than 5000 characters'},
    ]
}

const CharacterDrawer = ({
                             story,
                             drawerVisibility,
                             setdDrawerVisibility,
                             characterDetail,
                             setCharacterDetail,
                             characters,
                             actionType,
                             setActionType,
                             onSuccess,
                             onFailure
                         }: any) => {

    const [form] = Form.useForm();
    const formValueObj = Form.useWatch([], form);

    const [submittables, setSubmittable] = useState<boolean>(false);
    const [loadingBtn, setLoadingBtn] = useState<boolean>(false);
    const [properties, setProperties] = useState<any[]>([]);
    const [characterStatus, setCharacterStatus] = useState<string>("inactive");
    const [characterType, setCharacterType] = useState<string>("ai_character");

    // for image
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [images, setImages] = useState<UploadFile[]>([]);

    // images
    const getBase64 = (file: RcFile): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

    const handleCancel = () => setPreviewOpen(false);

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as RcFile);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
        try {
            // Tạo một danh sách fileList tạm thời để hiển thị ngay lập tức
            const tempFileList = newFileList.map(file => ({
                ...file,
                status: 'uploading' as UploadFile<any>['status'],
            }));
            setImages(tempFileList as UploadFile<any>[]);

            // Convert files sang WebP
            const webpFiles = await Promise.all(
                newFileList.map(async (file) => {
                    // Chỉ convert nếu là file mới upload
                    if (!file.originFileObj) {
                        return file;
                    }

                    try {
                        const webpFile = await convertToWebP(file.originFileObj as RcFile);
                        // Tạo preview URL cho file đã convert
                        const previewUrl = await getBase64(webpFile);

                        return {
                            ...file,
                            originFileObj: webpFile,
                            status: 'done',
                            preview: previewUrl,
                            url: previewUrl,
                            fileName: webpFile.name
                        };
                    } catch (error) {
                        return {
                            ...file,
                            status: 'error',
                            error: new Error('Failed to convert image')
                        };
                    }
                })
            );

            setImages(webpFiles as UploadFile<any>[]);
        } catch (error) {
            console.error('Error processing files:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to process image'
            });
        }
    };

    const uploadButton = (
        <div>
            {/* <PlusOutlined rev={1} /> */}
            <Icon name="plus" size={16}/>
            <div style={{marginTop: 8}}>Upload</div>
        </div>
    );

    useEffect(() => {
        form.validateFields({validateOnly: true})
            .then(
                () => {
                    setSubmittable(true)
                },
                () => {
                    setSubmittable(false)
                },
            );
    }, [formValueObj]);

    useEffect(() => {
        if (actionType === ActionType.CREATE) {
            form.resetFields();
            setCharacterStatus("inactive");
            form.setFieldValue("status", "inactive");
            form.setFieldValue("type", "ai_character");
            setProperties([{
                key: "1",
                noNumber: "1",
                propertyName: "fullDescription",
                dataType: "value",
                value: ""
            }]);
            setImages([]);
        }
    }, [actionType]);

    useEffect(() => {
        if (!characterDetail) {
            form.setFieldValue("status", "inactive");
            form.setFieldValue("type", "ai_character");
            return;
        }
        if (characterDetail?.avatarUrl && characterDetail?.avatarUrl?.trim().length > 0) {
            const urlTemp = characterDetail?.avatarUrl.split("/");
            setImages([{
                uid: "1",
                name: urlTemp[urlTemp.length - 1],
                status: 'done',
                url: characterDetail?.avatarUrl
            }]);
        } else {
            setImages([]);
        }
        let property = JSON.parse(characterDetail.description);
        let count = 0;
        const tempProperties = [];
        for (let key in property) {
            key = key.replace("<property name>", "").trim();
            if (key.length === 0) {
                continue;
            }
            count++;
            const value = property[key];
            if (Array.isArray(value)) {
                tempProperties.push({
                    key: count + "",
                    noNumber: count,
                    propertyName: key,
                    dataType: `array`,
                    value: value.join(";")
                });
                continue;
            }
            tempProperties.push({
                key: count + "",
                noNumber: count,
                propertyName: key,
                dataType: `value`,
                value: value
            });
        }
        setProperties(tempProperties);
        setCharacterStatus(characterDetail.status);
        setCharacterType(characterDetail.type);
        form.setFieldsValue({...characterDetail});
    }, [characterDetail]);

    const onClose = () => {
        form.resetFields();
        setCharacterDetail(null);
        setCharacterStatus("inactive");
        form.setFieldValue("status", "inactive");
        form.setFieldValue("type", "ai_character");
        setCharacterType("ai_character");
        setSubmittable(false);
        setImages([]);
        setdDrawerVisibility(false);
        setActionType("");
        setProperties([]);
    }

    const saveCharacter = async () => {
        try {
            setLoadingBtn(true);
            let response;
            let characterProperty: any = {};
            if (properties && properties?.length > 0) {
                for (let i = 0; i < properties.length; i++) {
                    const item = properties[i];
                    const key = item.propertyName.trim().replaceAll("<property name>", "").trim();
                    if (key.length === 0) {
                        continue;
                    }
                    let value = item.value?.trim();
                    if (value?.length === 0) {
                        onFailure("Please complete character properties. There are some properties doesn't have value");
                        setLoadingBtn(false);
                        return;
                    }
                    if (item.dataType == 'array') {
                        value = item.value.split(";")
                            .map((t: string) => t ? t.trim() : null)
                            .filter((t: string) => t !== null && t !== undefined && t !== '');
                    }
                    characterProperty[item.propertyName] = value;
                }
            }
            const requestModel: any = {
                name: formValueObj.name?.trim(),
                generalInformation: formValueObj.generalInformation?.trim(),
                status: characterStatus,
                type: characterType,
                properties: characterProperty
            }
            if (characterStatus === "active" && characterType === "user_character") {
                const tempChars = characters.map((c: any) => {
                    if (c.id === characterDetail?.id) return {...characterDetail, ...requestModel}
                    return c;
                });
                if (actionType === ActionType.CREATE) {
                    tempChars.push(requestModel);
                }
                const count = tempChars
                    .filter((c: any) => c.status === "active" && (actionType === ActionType.CREATE || c.id !== characterDetail?.id) && c.type === "user_character")
                    .length;
                if (count > 1) {
                    onFailure("There must be only 1 active user character at a time");
                    setLoadingBtn(false);
                    return;
                } else {
                    if (requestModel.name !== "{{user.name}}") {
                        onFailure("User character must be '{{user.name}}', please update the name following this rule and activate character!");
                        setLoadingBtn(false);
                        return;
                    }
                }
            }
            if (actionType === ActionType.CREATE) {
                response = await createCharacter(story.id, {
                    data: JSON.stringify(requestModel),
                    image: images.length > 0 ? images?.[0]?.originFileObj : null
                });
            } else {
                const requestBody: any = {};
                if (images?.[0] && images?.[0].url === characterDetail.avatarUrl) {
                    requestModel["avatarUrl"] = characterDetail.avatarUrl;
                } else {
                    requestBody["image"] = images?.[0]?.originFileObj;
                }
                requestBody["data"] = JSON.stringify(requestModel);

                response = await updateCharacter(story.id, characterDetail.id, requestBody);
            }

            setLoadingBtn(false);
            if (response?.status === 200) {
                onSuccess();
                onClose();
                return;
            }
        } catch (err) {
            console.log(err);
            onFailure(err);
        }
        setLoadingBtn(false);
    }

    return (
        <Drawer
            title={(actionType === ActionType.UPDATE ? "Update" : "Create") + " character"}
            className='character-drawer'
            width={850}
            onClose={() => {
                setdDrawerVisibility(false)
            }}
            open={drawerVisibility}
            closeIcon={false}
            footer={
                <div className='d-flex justify-content-center'>
                    <Button onClick={saveCharacter} type="primary" className='me-3' disabled={!submittables}
                            loading={loadingBtn}>
                        Save
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </div>
            }
        >
            <Form layout="vertical" autoComplete='off' form={form}>
                <Row gutter={16} className='d-flex justify-content-center'>
                    <Form.Item
                        name="image"
                        label="Image"
                    >
                        <Upload
                            listType="picture-card"
                            fileList={images}
                            onPreview={handlePreview}
                            onChange={handleChange}
                            multiple={false}
                            accept="image/*"
                        >
                            {images.length >= 1 ? null : uploadButton}
                        </Upload>
                        <Modal open={previewOpen} footer={null} onCancel={handleCancel}>
                            <img alt="example" style={{width: '100%'}} src={previewImage}/>
                        </Modal>
                    </Form.Item>
                </Row>
                <Row gutter={16} className='mb-3'>
                    <Col span={24}>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={characterDrawerValidation.name}
                        >
                            <Input placeholder="Please enter name"/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} className='mb-3'>
                    <Col span={12}>
                        <Form.Item
                            name="type"
                            label="Type"
                            rules={characterDrawerValidation.type}
                        >
                            <Select placeholder="Please select a type" value={characterType}
                                    onChange={(e) => setCharacterType(e)}>
                                <Option value="user_character">User character</Option>
                                <Option value="ai_character">AI character</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="status"
                            label="Status"
                            rules={characterDrawerValidation.status}
                        >
                            <Select placeholder="Please select a status" value={characterStatus}
                                    onChange={(e) => setCharacterStatus(e)}>
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="generalInformation"
                            label="Short description"
                            rules={characterDrawerValidation.generalInformation}
                        >
                            <TextArea rows={4} placeholder="Enter short description" maxLength={5000}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="customProperties"
                            label="Custom propeties"
                        >
                            <CharacterProperty properties={properties} setProperties={setProperties}/>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    )

}

export default CharacterDrawer;
